import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const POLICIES = [
  { key: 'privacy', label: 'Privacy Policy',     file: './agreement/Privacy_Policy.txt' },
  { key: 'system',  label: 'System Requirements', file: './agreement/System_Requirements.txt' },
  { key: 'terms',   label: 'Terms of Use',        file: './agreement/Terms_of_use.txt' },
];

const Policy = ({ onAgree }) => {
  const navigate = useNavigate();
  const [texts, setTexts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [checked, setChecked] = useState({ privacy: false, system: false, terms: false });

  useEffect(() => {
    POLICIES.forEach(({ key, file }) => {
      fetch(file)
        .then(r => r.text())
        .then(text => setTexts(prev => ({ ...prev, [key]: text })))
        .catch(console.error);
    });
  }, []);

  const allChecked = POLICIES.every(p => checked[p.key]);

  const handleAcceptAll = () => setChecked({ privacy: true, system: true, terms: true });

  const handleContinue = () => {
    if (!allChecked) return;
    const agreeTime = new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
    onAgree({
      policy_1: { agree: true, agreeTime },
      policy_2: { agree: true, agreeTime },
      policy_3: { agree: true, agreeTime },
    });
  };

  const toggleCheck = (key) => setChecked(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">

        {/* Branding */}
        <div className="text-center mb-7">
          <img
            src="/dashboard/logoonly.png"
            alt="Logo"
            className="h-10 mx-auto mb-3"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Before you continue
          </h1>
          <p className="text-sm text-gray-500 mt-1.5">
            Review and agree to our policies to create your account.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">

          {/* Quick accept bar */}
          <div className="px-5 py-3.5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {allChecked ? (
                <span className="text-sm font-semibold text-green-600 flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600 text-xs font-bold">✓</span>
                  All policies accepted
                </span>
              ) : (
                <span className="text-sm text-gray-600">Quick-accept all at once</span>
              )}
            </div>
            <button
              onClick={handleAcceptAll}
              disabled={allChecked}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${
                allChecked
                  ? 'bg-green-100 text-green-600 cursor-default'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow active:scale-95'
              }`}
            >
              {allChecked ? 'All Accepted ✓' : 'Accept All'}
            </button>
          </div>

          {/* Policy rows */}
          <div className="divide-y divide-gray-100">
            {POLICIES.map(({ key, label }) => (
              <div key={key}>
                {/* Main row */}
                <div className="px-5 py-4 flex items-center gap-3">
                  {/* Checkbox */}
                  <button
                    type="button"
                    onClick={() => toggleCheck(key)}
                    className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all duration-150 ${
                      checked[key]
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300 hover:border-blue-400 bg-white'
                    }`}
                    aria-label={`Agree to ${label}`}
                  >
                    {checked[key] && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>

                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{label}</p>
                    {checked[key] && (
                      <p className="text-xs text-green-500 font-medium mt-0.5">Agreed</p>
                    )}
                  </div>

                  {/* View/Hide toggle */}
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === key ? null : key)}
                    className="flex-shrink-0 flex items-center gap-1 text-xs font-medium text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    {expanded === key ? 'Hide' : 'View'}
                    <svg
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded === key ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Expandable content */}
                {expanded === key && (
                  <div className="px-5 pb-4">
                    <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
                      <div className="p-4 max-h-52 overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-xs leading-relaxed text-gray-600 font-sans">
                          {texts[key] || 'Loading…'}
                        </pre>
                      </div>
                      {/* Inline agree button inside expanded view */}
                      <div className="px-4 py-2.5 border-t border-gray-100 flex justify-end">
                        <button
                          type="button"
                          onClick={() => { toggleCheck(key); setExpanded(null); }}
                          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            checked[key]
                              ? 'bg-gray-100 text-gray-400'
                              : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                          }`}
                        >
                          {checked[key] ? 'Agreed ✓' : 'Agree & Close'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleContinue}
              disabled={!allChecked}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                allChecked
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md active:scale-95'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Continue
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4 px-4">
          By continuing, you agree to our Privacy Policy, System Requirements, and Terms of Use.
        </p>
      </div>
    </div>
  );
};

export default Policy;
